// src/contexts/UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({
    fullName: "",
    id: "N/A",
    depositBalance: 0.0,
    bonusBalance: 0.0,
  });
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const REFRESH_COOLDOWN = 1000; // 1 seconds cooldown between refreshes

  // Centralized logout function
  const logout = useCallback(() => {
    // Clear all authentication storage
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    delete axiosInstance.defaults.headers.common['Authorization'];
    
    // Reset user state
    setUserData({
      fullName: "",
      id: "N/A",
      depositBalance: 0.0,
      bonusBalance: 0.0,
    });
    
    setUserError("Session expired. Please log in again.");
    
    // Emit event for other components to handle redirect
    window.dispatchEvent(new Event('userLoggedOut'));
  }, []);

  // Basic XSS sanitization helper (optional)
  const sanitizeString = useCallback((str) => {
    if (typeof str !== 'string') return str;
    // Remove script tags and basic XSS vectors
    return str
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:\s*[^"']*/gi, '');
  }, []);

  const fetchUserProfile = useCallback(async () => {
    setUserLoading(true);
    setUserError(null);
    
    try {
      const response = await axiosInstance.get("/user/profile");
      
      if (response.data.success) {
        const user = response.data.user;
        
        setUserData({
          fullName: sanitizeString(user.fullName) || "Guest",
          id: user.id || "N/A",
          depositBalance: parseFloat(user.depositBalance) || 0.0,
          bonusBalance: parseFloat(user.bonusBalance) || 0.0,
        });
      } else {
        setUserError(response.data.message || "Failed to fetch user profile.");
        setUserData({
          fullName: "",
          id: "N/A",
          depositBalance: 0.0,
          bonusBalance: 0.0,
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Network error fetching user profile.";
      setUserError(errorMessage);
      setUserData({
        fullName: "",
        id: "N/A",
        depositBalance: 0.0,
        bonusBalance: 0.0,
      });
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    } finally {
      setUserLoading(false);
      setLastRefreshTime(Date.now());
    }
  }, [logout, sanitizeString]);

  // Rate-limited refresh function
  const refreshUserData = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTime < REFRESH_COOLDOWN) {
      return;
    }
    fetchUserProfile();
  }, [fetchUserProfile, lastRefreshTime]);

  useEffect(() => {
    // Fetch user data when the provider mounts
    fetchUserProfile();

    // Set up CSRF token handling if your backend uses it
    const setupCsrfProtection = async () => {
    };

    setupCsrfProtection();
  }, [fetchUserProfile]);

  // Optional: Auto-refresh user data periodically (e.g., every 5 minutes)
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      if (!userLoading) {
        refreshUserData();
      }
    }, 1000); // 5 minutes

    return () => clearInterval(autoRefreshInterval);
  }, [userLoading, refreshUserData]);

  // Listen for logout events from other components
  useEffect(() => {
    const handleLogoutEvent = () => {
      logout();
    };

    window.addEventListener('forceLogout', handleLogoutEvent);
    return () => window.removeEventListener('forceLogout', handleLogoutEvent);
  }, [logout]);

  return (
    <UserContext.Provider value={{ 
      userData, 
      userLoading, 
      userError, 
      refreshUserData, 
      setUserData,
      logout
    }}>
      {children}
    </UserContext.Provider>
  );
};